import { useState, useEffect, useRef } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    TextField,
    Box,
    Paper,
    CircularProgress
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { getRequest, postRequest } from 'src/services';
import { useSnackbar } from 'src/components/snackbar';
import Iconify from 'src/components/iconify';
import { endpoints } from 'src/utils/axios';
import { useAuthContext } from 'src/auth/hooks';
import { timeFormatter } from 'src/services/fetching';

export const ChatAdminUserModal = ({ open, onClose, row }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [ loading, setLoading ] = useState(false);
    const [ messages, setMessages ] = useState([]);
    const [ input, setInput ] = useState("");
    const chatRef = useRef(null);

    const { user } = useAuthContext();
    const userId = user?.id;
    const userRole = user?.roles?.[ 0 ]?.name;

    const getMessages = async () => {
        if (!row?.id) return;
        setLoading(true);
        try {
            const data = await getRequest(
                `${endpoints.admin_dashboard.support_tickets.get_messages}/${row.id}/messages`
            );
            if (data) {
                setMessages(data.map(msg => ({
                    ...msg,
                    status: "sent" // Mark existing messages as sent
                })));
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
        setLoading(false);
    };

    const sendMessage = async () => {
        if (!input.trim() || !row?.id) return;

        const newMessage = {
            id: Math.random(), // Temporary ID
            message: input,
            sender_id: userId,
            sender_name: user?.name || userRole,
            sender_type: "admin",
            status: "loading", // Message sending in progress
        };

        setMessages(prev => [ ...prev, newMessage ]);
        setInput("");

        try {
            const response = await postRequest(
                `${endpoints.admin_dashboard.support_tickets.post_messages}/${row.id}/messages`,
                { message: input, sender: userRole },
                true
            );

            if (response) {
                setMessages(prev =>
                    prev.map(msg =>
                        msg.id === newMessage.id ? { ...msg, status: "delivered" } : msg
                    )
                );

                enqueueSnackbar({ variant: 'success', message: "Message sent successfully" });

                setTimeout(() => {
                    if (chatRef.current) {
                        chatRef.current.scrollTop = chatRef.current.scrollHeight;
                    }
                }, 100);
            }
        } catch (err) {
            console.error('Error:', err);
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === newMessage.id ? { ...msg, status: "failed" } : msg
                )
            );
        }
    };

    useEffect(() => {
        if (open && row?.id) {
            getMessages();
        }
    }, [ open, row?.id ]);

    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [ messages ]);

    return (
        <Dialog
            open={ open }
            onClose={ (event, reason) => {
                if (reason !== 'backdropClick') {
                    onClose();
                }
            } }
            fullWidth
            maxWidth="md"
        >
            <DialogTitle
                sx={ {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                } }
            >
                <Typography sx={ { padding: '7px', fontWeight: '700', backgroundColor: '#F4F6F8', color: '#637381', borderRadius: '8px' } }>
                    Chat Session
                </Typography>
                <IconButton onClick={ onClose }>
                    <Iconify icon="mdi:close" />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Paper
                    elevation={ 2 }
                    sx={ {
                        height: 400,
                        overflowY: 'auto',
                        backgroundColor: '#F9FAFB',
                        padding: 2,
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column'
                    } }
                    ref={ chatRef }
                >
                    { messages.length > 0 ? (
                        messages.map((msg, index) => {
                            const isMe = msg.sender_id === userId;
                            return (
                                <Box
                                    key={ msg.id }
                                    sx={ {
                                        maxWidth: '70%',
                                        alignSelf: isMe ? 'flex-end' : 'flex-start',
                                        backgroundColor: isMe ? '#E0F7FA' : '#FDF6DE',
                                        padding: '10px',
                                        borderRadius: '10px',
                                        marginBottom: '8px',
                                        boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.08)',
                                        textAlign: isMe ? 'right' : 'left',
                                        position: 'relative'
                                    } }
                                >
                                    <Typography variant="body2">
                                        <span style={ { fontWeight: "bold" } }>{ isMe ? "You" : msg.sender_name }:</span> { msg.message || '' }
                                    </Typography>

                                    {/* Status Icon (Loader / Single tick / Double tick) with Timestamp */ }
                                    { isMe && (
                                        <Box sx={ { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.5 } }>
                                            <Typography variant="caption" sx={ { color: '#666', mr: 0.5 } }>

                                                { timeFormatter(msg?.time_stamp) }
                                            </Typography>
                                            { msg.status === "loading" && <CircularProgress size={ 10 } /> }
                                            { msg.status === "sent" && <Typography variant="caption">✓</Typography> }
                                            { msg.status === "delivered" && <Typography variant="caption">✓✓</Typography> }
                                            { msg.status === "failed" && <Typography variant="caption" color="error">❌</Typography> }
                                        </Box>
                                    ) }

                                </Box>
                            );
                        })
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No messages yet
                        </Typography>
                    ) }
                </Paper>

                {/* Input and Send Button */ }
                <Stack direction="row" alignItems="center" spacing={ 2 } sx={ { mt: 2 } }>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Type a message..."
                        value={ input }
                        onChange={ (e) => setInput(e.target.value) }
                        onKeyDown={ (e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                sendMessage();
                            }
                        } }
                    />

                    <LoadingButton
                        variant="contained"
                        loading={ loading }
                        onClick={ sendMessage }
                    >
                        Send
                    </LoadingButton>

                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" color="inherit" onClick={ onClose }>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};
